import { type RefObject, useEffect, useState } from 'react';

/**
 * Hook that tracks whether an element is visible on screen using Intersection Observer
 * @param ref React ref object pointing to the element to monitor
 * @param rootMargin CSS margin around the root element (defaults to '0px')
 * @returns Boolean indicating whether the element is currently visible on screen
 */
export const useOnScreen = (ref: RefObject<HTMLElement>, rootMargin: string = '0px'): boolean => {
  // State and setter for storing whether element is visible
  const [isOnScreen, setIsOnScreen] = useState<boolean>(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsOnScreen(entry.isIntersecting);
      },
      { rootMargin }
    );

    observer.observe(node);
    return () => {
      observer.unobserve(node);
    };
  }, [ref, rootMargin]);

  return isOnScreen;
};
